import React from 'react';
import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Botao from '@/components/Botao';
import PageHeader from '@/components/layout/PageHeader';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import Modal from '@/components/layout/Modal';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoNumero from '@/components/Formulario/CampoNumero';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoCheckbox from '@/components/Formulario/CampoCheckbox';
import SeletorEscopoAppliances from '@/components/Formulario/SeletorEscopoAppliances';
import {
    mdiAccountGroup,
    mdiAlertCircle,
    mdiCheckCircle,
    mdiClipboardTextClock,
    mdiFileExport,
    mdiFileImport,
    mdiLoading,
    mdiMagnify,
    mdiPencil,
    mdiPlus,
    mdiTrashCan,
} from '@mdi/js';
import Icon from '@mdi/react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import Papa from 'papaparse';
import { toastError, toastSuccess, toastWarning } from '@/lib/toast';
import { extractApiError } from '@/lib/apiError';
import useWindowSize from '@/lib/useWindowSize';
import { useDebounce } from '@/lib/useDebounce';
import type {
    RadiusUserGroupDTO,
    RadiusUserGroupRestricaoDTO,
    RadiusUserDTO,
    RadiusUserConexaoFiltro,
    RadiusUserListDTO,
    RadiusServico,
    RadiusGroupApplianceScope,
} from '@webip/shared/src/dto/radius_user';
import CampoSenha from '@/components/Formulario/CampoSenha';
import StatusDot from '@/components/layout/StatusDot';
import type { RadiusUserStatusDTO } from '@webip/shared/src/dto/connection_status';
import { formatDuration } from '@/lib/formatDuration';
import Descricao from '@/components/tipografia/Descricao';
import TentativasModal from './TentativasModal';
import CampoVlan from './CampoVlan';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const DIAS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const SERVICOS_OPTIONS: { value: RadiusServico; label: string; desc: string }[] = [
    { value: 'hotspot', label: 'Hotspot', desc: 'Captive portal (NAS-Port-Type=Ethernet/Wireless)' },
    { value: 'ppp', label: 'VPN PPP', desc: 'PPTP / L2TP / SSTP / OpenVPN (NAS-Port-Type=Virtual)' },
    { value: 'wifi', label: 'Wi-Fi 802.1X', desc: 'EAP/PEAP wireless (NAS-Port-Type=Wireless-802.11)' },
];

const VPN_TIPOS_PPP: { value: string; label: string }[] = [
    { value: 'pptp', label: 'PPTP' },
    { value: 'l2tp', label: 'L2TP' },
    { value: 'sstp', label: 'SSTP' },
    { value: 'openvpn', label: 'OpenVPN' },
];

function toggleItem<T>(arr: T[], item: T): T[] {
    return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
}

function StatusBadge({ ativo }: { ativo: boolean }) {
    return (
        <span
            style={{
                padding: '2px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 600,
                background: ativo ? 'var(--status-online-bg)' : 'var(--status-offline-bg)',
                color: ativo ? 'var(--status-online)' : 'var(--status-offline)',
            }}
        >
            {ativo ? 'Ativo' : 'Inativo'}
        </span>
    );
}

// ─── Restrições de horário ────────────────────────────────────────────────────

function RestricaoEditor({
    restricoes,
    onChange
}: {
    restricoes: RadiusUserGroupRestricaoDTO[];
    onChange: (r: RadiusUserGroupRestricaoDTO[]) => void;
}) {
    const add = () =>
        onChange([
            ...restricoes,
            { hora_inicio: '08:00', hora_fim: '18:00', dias_semana: [1, 2, 3, 4, 5] },
        ]);

    const remove = (i: number) => onChange(restricoes.filter((_, idx) => idx !== i));

    const update = (i: number, patch: Partial<RadiusUserGroupRestricaoDTO>) =>
        onChange(restricoes.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

    const toggleDia = (i: number, dia: number) => {
        const r = restricoes[i];
        const dias = r.dias_semana.includes(dia)
            ? r.dias_semana.filter((d) => d !== dia)
            : [...r.dias_semana, dia];
        update(i, { dias_semana: dias });
    };

    return (
        <VBox style={{ gap: '8px' }}>
            <HBox style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: '14px' }}>Restrições de horário</span>
                <Botao variant="outline-primary" size="sm" icon={mdiPlus} onClick={add}>
                    Adicionar janela
                </Botao>
            </HBox>
            {restricoes.length === 0 && (
                <Descricao>Sem restrições — acesso permitido 24h/7d</Descricao>
            )}
            {restricoes.map((r, i) => (
                <VBox
                    key={i}
                    style={{ border: '1px solid var(--tc-color-border)', borderRadius: '6px', padding: '10px', gap: '8px' }}
                >
                    <HBox style={{ gap: '10px', alignItems: 'flex-end' }}>
                        <CampoTexto
                            label="Início"
                            type="time"
                            value={r.hora_inicio}
                            onChange={(v) => update(i, { hora_inicio: v })}
                            style={{ maxWidth: '110px' }}
                        />
                        <CampoTexto
                            label="Fim"
                            type="time"
                            value={r.hora_fim}
                            onChange={(v) => update(i, { hora_fim: v })}
                            style={{ maxWidth: '110px' }}
                        />
                        <CampoTexto
                            label="Descrição"
                            value={r.descricao ?? ''}
                            onChange={(v) => update(i, { descricao: v })}
                        />
                        <Botao
                            variant="outline-danger"
                            size="sm"
                            icon={mdiTrashCan}
                            onClick={() => remove(i)}
                            style={{ marginBottom: '2px' }}
                        />
                    </HBox>
                    <HBox style={{ gap: '6px', flexWrap: 'wrap' }}>
                        {DIAS.map((dia, d) => (
                            <button
                                key={d}
                                type="button"
                                onClick={() => toggleDia(i, d)}
                                style={{
                                    border: '1px solid',
                                    borderRadius: '4px',
                                    padding: '2px 8px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    background: r.dias_semana.includes(d)
                                        ? 'var(--tc-color-primary)'
                                        : 'transparent',
                                    color: r.dias_semana.includes(d)
                                        ? '#fff'
                                        : 'var(--tc-color-text)',
                                    borderColor: 'var(--tc-color-primary)',
                                }}
                            >
                                {dia}
                            </button>
                        ))}
                    </HBox>
                </VBox>
            ))}
        </VBox>
    );
}

// ─── Modal de grupo ───────────────────────────────────────────────────────────

function GrupoModal({
    show,
    grupo,
    onClose,
}: {
    show: boolean;
    grupo: RadiusUserGroupDTO | null;
    onClose: () => void;
}) {
    const qc = useQueryClient();
    const isEdit = grupo !== null;

    const syncRadiusCaches = React.useCallback(async () => {
        await Promise.all([
            qc.invalidateQueries(['radius-groups']),
            qc.invalidateQueries(['radius-users']),
        ]);
        await Promise.all([
            qc.refetchQueries(['radius-groups']),
            qc.refetchQueries(['radius-users']),
        ]);
    }, [qc]);

    const [nome, setNome] = React.useState(grupo?.nome ?? '');
    const [descricao, setDescricao] = React.useState(grupo?.descricao ?? '');
    const [horasDia, setHorasDia] = React.useState<number | ''>(grupo?.horas_dia ?? '');
    const [ativo, setAtivo] = React.useState(grupo?.ativo ?? true);
    const [servicos, setServicos] = React.useState<RadiusServico[]>(grupo?.servicos ?? []);
    const [vpnTipos, setVpnTipos] = React.useState<string[]>(grupo?.vpn_tipos ?? []);
    const [restricoes, setRestricoes] = React.useState<RadiusUserGroupRestricaoDTO[]>(
        grupo?.restricoes ?? [],
    );
    const [isDefault, setIsDefault] = React.useState(grupo?.is_default ?? false);
    const [applianceScope, setApplianceScope] = React.useState<RadiusGroupApplianceScope>(
        grupo?.appliance_scope ?? 'all',
    );
    const [selectedApplianceIds, setSelectedApplianceIds] = React.useState<number[]>(
        grupo?.appliance_ids ?? [],
    );
    const [vlanId, setVlanId] = React.useState<number | null>(grupo?.vlan_id ?? null);
    const [vlanOk, setVlanOk] = React.useState(true);

    const mutSave = useMutation({
        mutationFn: async (): Promise<RadiusUserGroupDTO> => {
            const body: Partial<RadiusUserGroupDTO> = {
                nome,
                descricao: descricao || undefined,
                horas_dia: horasDia === '' ? null : Number(horasDia),
                ativo,
                is_default: isDefault,
                servicos,
                vpn_tipos: servicos.includes('ppp') ? vpnTipos : [],
                restricoes,
                appliance_scope: applianceScope,
                appliance_ids: applianceScope === 'specific' ? selectedApplianceIds : [],
                vlan_id: vlanId,
            };
            if (isEdit) {
                const res = await axios.put<{ ok: boolean; result: RadiusUserGroupDTO }>(
                    `/api/radius/groups/${grupo!.id}`, body, { withCredentials: true },
                );
                return res.data.result;
            } else {
                const res = await axios.post<{ ok: boolean; result: RadiusUserGroupDTO }>(
                    '/api/radius/groups', body, { withCredentials: true },
                );
                return res.data.result;
            }
        },
        onSuccess: async (savedGroup) => {
            qc.setQueryData<RadiusUserGroupDTO[]>(['radius-groups'], (old) => {
                if (!old) return [savedGroup];
                if (isEdit) {
                    return old.map((g) => (g.id === savedGroup.id ? savedGroup : g));
                }
                return [...old, savedGroup];
            });
            toastSuccess(isEdit ? 'Grupo atualizado!' : 'Grupo criado!');
            await syncRadiusCaches();
            onClose();
        },
        onError: (err) => {
            const { message } = extractApiError(err, 'Erro ao salvar grupo');
            toastError(message);
        },
    });

    const onSave = () => {
        if (!nome.trim()) {
            toastError('Nome é obrigatório');
            return;
        }
        if (!vlanOk) {
            toastError('VLAN deve ser um número entre 1 e 4094');
            return;
        }
        mutSave.mutate();
    };

    return (
        <Modal
            show={show}
            title={isEdit ? 'Editar grupo RADIUS' : 'Novo grupo RADIUS'}
            onClose={onClose}
            onConfirm={onSave}
            onCancel={onClose}
        >
            <VBox style={{ gap: '12px', minWidth: '400px' }}>
                <CampoTexto label="Nome *" value={nome} onChange={setNome} required />
                {isEdit && !isDefault && (
                    <CampoTexto
                        label="Slug (realm)"
                        value={grupo!.slug ?? ''}
                        readOnly
                        tooltipText="Gerado automaticamente a partir do nome. Usado como realm no formato slug\\usuario."
                    />
                )}
                {isDefault && (
                    <Descricao>
                        Usuários deste grupo autenticam com username simples, sem prefixo de realm.
                    </Descricao>
                )}
                <CampoTexto label="Descrição" value={descricao} onChange={setDescricao} />
                <CampoNumero
                    label="Limite de horas/dia (0 = sem limite)"
                    value={horasDia === '' ? '0' : String(horasDia)}
                    onChange={(v) => {
                        const n = parseFloat(v);
                        setHorasDia(isNaN(n) || n === 0 ? '' : n);
                    }}
                    decimais={1}
                    somentePositivos
                />
                <CampoCheckbox text="Ativo" value={ativo} onChange={setAtivo} />
                <CampoCheckbox
                    text="Grupo padrão (autenticação sem prefixo de realm)"
                    value={isDefault}
                    onChange={setIsDefault}
                />

                {/* Serviços permitidos */}
                <VBox style={{ gap: '6px' }}>
                    <label style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-label)' }}>
                        Serviços permitidos
                        <span style={{ fontWeight: 400, color: 'var(--color-muted)', marginLeft: '6px' }}>
                            (vazio = sem restrição)
                        </span>
                    </label>
                    {SERVICOS_OPTIONS.map((opt) => (
                        <VBox key={opt.value} style={{ gap: '2px' }}>
                            <HBox style={{ gap: '8px', alignItems: 'center' }}>
                                <input
                                    type="checkbox"
                                    id={`svc-${opt.value}`}
                                    checked={servicos.includes(opt.value)}
                                    onChange={() => setServicos((prev) => toggleItem(prev, opt.value))}
                                />
                                <label htmlFor={`svc-${opt.value}`} style={{ cursor: 'pointer', userSelect: 'none', fontWeight: 500 }}>
                                    {opt.label}
                                </label>
                                <span style={{ fontSize: '12px', color: 'var(--color-muted)' }}>{opt.desc}</span>
                            </HBox>

                            {/* Sub-seletor de tipos PPP quando "ppp" está marcado */}
                            {opt.value === 'ppp' && servicos.includes('ppp') && (
                                <HBox style={{ gap: '12px', paddingLeft: '24px', flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: '12px', color: 'var(--color-muted)', alignSelf: 'center' }}>
                                        Protocolos (vazio = todos):
                                    </span>
                                    {VPN_TIPOS_PPP.map((t) => (
                                        <HBox key={t.value} style={{ gap: '4px', alignItems: 'center' }}>
                                            <input
                                                type="checkbox"
                                                id={`vpntipo-${t.value}`}
                                                checked={vpnTipos.includes(t.value)}
                                                onChange={() => setVpnTipos((prev) => toggleItem(prev, t.value))}
                                            />
                                            <label htmlFor={`vpntipo-${t.value}`} style={{ cursor: 'pointer', userSelect: 'none', fontSize: '13px' }}>
                                                {t.label}
                                            </label>
                                        </HBox>
                                    ))}
                                </HBox>
                            )}
                        </VBox>
                    ))}
                </VBox>

                <RestricaoEditor restricoes={restricoes} onChange={setRestricoes} />

                {/* Escopo de appliances */}
                <SeletorEscopoAppliances
                    includeNone
                    scope={applianceScope}
                    onScopeChange={setApplianceScope}
                    selectedIds={selectedApplianceIds}
                    onSelectedIdsChange={setSelectedApplianceIds}
                />

                <CampoVlan
                    value={vlanId}
                    onChange={setVlanId}
                    onValidChange={setVlanOk}
                    scope={applianceScope}
                    applianceIds={selectedApplianceIds}
                />
            </VBox>
        </Modal>
    );
}

// ─── Modal de gerenciamento de grupos ─────────────────────────────────────────

function GruposManagerModal({ onClose }: { onClose: () => void }) {
    const qc = useQueryClient();
    const [editGrupo, setEditGrupo] = React.useState<RadiusUserGroupDTO | null | 'new'>(null);

    const syncRadiusCaches = React.useCallback(async () => {
        await Promise.all([
            qc.invalidateQueries(['radius-groups']),
            qc.invalidateQueries(['radius-users']),
        ]);
        await Promise.all([
            qc.refetchQueries(['radius-groups']),
            qc.refetchQueries(['radius-users']),
        ]);
    }, [qc]);

    const queryGroups = useQuery(['radius-groups'], async () => {
        const res = await axios.get<{ ok: boolean; result: RadiusUserGroupDTO[] }>(
            '/api/radius/groups',
            { withCredentials: true },
        );
        return res.data.result ?? [];
    });

    const mutDelete = useMutation({
        mutationFn: async (id: number) => {
            await axios.delete(`/api/radius/groups/${id}`, { withCredentials: true });
        },
        onSuccess: async () => {
            toastSuccess('Grupo removido!');
            await syncRadiusCaches();
        },
        onError: (err) => {
            const { message } = extractApiError(err, 'Erro ao remover grupo');
            toastError(message);
        },
    });

    const grupos = queryGroups.data ?? [];

    const cabecalhos: DataTableHeader[] = [
        { label: 'Nome', column: 'nome' },
        {
            label: 'Slug',
            render: (g) => g.is_default
                ? (
                    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: '1px' }}>
                        <span style={{ color: 'var(--tc-color-text-muted, #999)' }}>{g.slug}</span>
                        <span style={{ fontSize: '10px', color: 'var(--tc-color-text-muted, #999)', fontStyle: 'italic' }}>não usado</span>
                    </span>
                )
                : <span>{g.slug}</span>,
        },
        { label: 'Horas/dia', render: (g) => g.horas_dia ? `${g.horas_dia}h` : '∞' },
        { label: 'Serviços', render: (g) => (g.servicos ?? []).length > 0 ? `${(g.servicos ?? []).length}` : '∞' },
        { label: 'Restrições', render: (g) => `${(g.restricoes ?? []).length}` },
        {
            label: 'Status',
            render: (g) => (
                <span style={{ display: 'inline-flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-start' }}>
                    <StatusBadge ativo={g.ativo} />
                    {g.is_default && (
                        <span style={{
                            padding: '1px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 600,
                            background: 'var(--tc-color-primary-bg, #e8f0fe)',
                            color: 'var(--tc-color-primary, #1a73e8)',
                        }}>
                            padrão
                        </span>
                    )}
                </span>
            ),
        },
        {
            label: '',
            render: (g) => (
                <HBox style={{ gap: '6px', justifyContent: 'flex-end' }}>
                    <Botao
                        variant="outline-primary"
                        size="sm"
                        icon={mdiPencil}
                        onClick={() => setEditGrupo(g)}
                    />
                    <Botao
                        variant="outline-danger"
                        size="sm"
                        icon={mdiTrashCan}
                        onClick={() => {
                            if (confirm(`Remover grupo "${g.nome}"?`)) mutDelete.mutate(g.id!);
                        }}
                        disabled={mutDelete.isLoading}
                    />
                </HBox>
            ),
        },
    ];

    return (
        <>
            <Modal show title="Grupos RADIUS" onClose={onClose} size="lg">
                <VBox style={{ gap: '12px', minWidth: '560px' }}>
                    <HBox style={{ justifyContent: 'flex-end' }}>
                        <Botao
                            variant="primary"
                            icon={mdiPlus}
                            onClick={() => setEditGrupo('new')}
                        >
                            Novo grupo
                        </Botao>
                    </HBox>
                    <DataTable
                        cabecalhos={cabecalhos}
                        linhas={grupos}
                        isLoading={queryGroups.isLoading}
                    />
                </VBox>
            </Modal>
            <GrupoModal
                key={editGrupo === 'new' ? 'new' : (editGrupo?.id ?? 'new')}
                show={editGrupo !== null}
                grupo={editGrupo === 'new' ? null : editGrupo}
                onClose={() => setEditGrupo(null)}
            />
        </>
    );
}

// ─── Modal de usuário ─────────────────────────────────────────────────────────

function UsuarioModal({
    show,
    usuario,
    grupoId,
    grupos,
    gatewaysEmUso,
    onClose,
}: {
    show: boolean;
    usuario: RadiusUserDTO | null;
    grupoId: number;
    grupos: RadiusUserGroupDTO[];
    /** Gateways já atribuídos a outros usuários (GET /radius/users/gateways). */
    gatewaysEmUso: string[];
    onClose: () => void;
}) {
    const qc = useQueryClient();
    const isEdit = usuario?.id && usuario.id > 0;

    const [selectedGrupoId, setSelectedGrupoId] = React.useState<number>(grupoId || grupos[0]?.id || 0);
    const [username, setUsername] = React.useState(usuario?.username ?? '');
    const [senha, setSenha] = React.useState('');
    const [nome, setNome] = React.useState(usuario?.nome ?? '');
    const [ativo, setAtivo] = React.useState(usuario?.ativo ?? true);
    const [framedIp, setFramedIp] = React.useState(usuario?.framed_ip ?? '');
    const [gatewayIp, setGatewayIp] = React.useState(usuario?.gateway_ip ?? '');
    const [vlanId, setVlanId] = React.useState<number | null>(usuario?.vlan_id ?? null);
    const [vlanOk, setVlanOk] = React.useState(true);
    const grupoSel = grupos.find((g) => g.id === selectedGrupoId);

    const mutSave = useMutation({
        mutationFn: async () => {
            const body: Partial<RadiusUserDTO> = {
                username,
                nome,
                ativo,
                grupo_id: selectedGrupoId,
                framed_ip: framedIp.trim() || null,
                gateway_ip: gatewayIp.trim() || null,
                vlan_id: vlanId,
                ...(senha ? { senha } : {}),
            };
            if (isEdit) {
                await axios.put(
                    `/api/radius/groups/${selectedGrupoId}/users/${usuario!.id}`,
                    body,
                    { withCredentials: true },
                );
            } else {
                await axios.post(`/api/radius/groups/${selectedGrupoId}/users`, body, {
                    withCredentials: true,
                });
            }
        },
        onSuccess: () => {
            toastSuccess(isEdit ? 'Usuário atualizado!' : 'Usuário criado!');
            qc.invalidateQueries(['radius-users']);
            onClose();
        },
        onError: (err) => {
            const { message } = extractApiError(err, 'Erro ao salvar usuário');
            toastError(message);
        },
    });

    const onSave = () => {
        if (!vlanOk) {
            toastError('VLAN deve ser um número entre 1 e 4094');
            return;
        }
        mutSave.mutate()
    };

    return (
        <Modal
            show={show}
            title={isEdit ? 'Editar usuário RADIUS' : 'Novo usuário RADIUS'}
            onClose={onClose}
            onConfirm={onSave}
            onCancel={onClose}
        >
            <VBox style={{ gap: '12px', minWidth: '360px' }}>
                {!isEdit && (
                    <CampoSeletor
                        label="Grupo *"
                        value={selectedGrupoId?.toString() || ''}
                        onChange={(v) => setSelectedGrupoId(Number(v))}
                        options={grupos.map((g) => ({ id: g.id!.toString(), label: `${g.nome} (${g.slug})` }))}
                    />
                )}
                <CampoTexto label="Nome *" value={nome} onChange={setNome} required />
                <CampoTexto
                    label="Username *"
                    value={username}
                    onChange={setUsername}
                    required
                    readOnly={isEdit}
                    disabled={isEdit}
                    autoComplete="off"
                    tooltipText="Identificador único dentro do grupo. Não pode ser alterado após criação."
                />
                <CampoSenha
                    label="Senha"
                    value={senha}
                    onChange={setSenha}
                    placeholder={isEdit ? '(Manter original)' : '(sem senha)'}
                    autoComplete="new-password"
                    hint="Opcional. Sem senha, qualquer cliente que informe este username é aceito — use só para contas migradas ou autenticação por username/MAC."
                />
                <CampoCheckbox text="Ativo" value={ativo} onChange={setAtivo} />

                <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '12px' }}>
                    <label style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-label)' }}>
                        Rede (opcional)
                    </label>
                </div>
                <CampoTexto
                    label="Endereço IP do cliente"
                    value={framedIp}
                    onChange={setFramedIp}
                    placeholder="(dinâmico — pool do servidor)"
                    tooltipText="IP fixo atribuído a este usuário na conexão (Framed-IP-Address). Vazio = IP dinâmico do pool."
                />
                <CampoTexto
                    label="Gateway / IP de saída"
                    value={gatewayIp}
                    onChange={setGatewayIp}
                    placeholder="(padrão do appliance)"
                    list="radius-gateways"
                    tooltipText="IP de terminação da sessão — o gateway que o cliente enxerga. Compartilhado entre usuários: use o mesmo valor para balancear vários usuários por poucos IPs de saída. Vazio = padrão do appliance."
                />
                <datalist id="radius-gateways">
                    {gatewaysEmUso.map((ip) => <option key={ip} value={ip} />)}
                </datalist>
                <CampoVlan
                    label="VLAN (override do grupo)"
                    value={vlanId}
                    onChange={setVlanId}
                    onValidChange={setVlanOk}
                    scope={grupoSel?.appliance_scope ?? 'all'}
                    applianceIds={grupoSel?.appliance_ids ?? []}
                    herdada={grupoSel?.vlan_id ?? null}
                />

            </VBox>
        </Modal>
    );
}

// ─── Importar usuários via CSV ─────────────────────────────────────────────────

type CsvUsuarioRow = { usuario: string; senha: string; nome: string; nome_grupo: string; ativo: string };
type CsvImportError = { linha: number; usuario: string; nome: string; nome_grupo: string; erro: string };
type CsvImportResult = { ok: boolean; imported: number; errors: CsvImportError[] };

function parseUsuariosCsvPreview(text: string): { rows: CsvUsuarioRow[]; error: string | null } {
    const result = Papa.parse<Record<string, string>>(text, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => h.trim().toLowerCase(),
        transform: (v) => v.trim(),
    });

    if (result.errors.length > 0 && result.data.length === 0) {
        return { rows: [], error: `Erro ao ler CSV: ${result.errors[0].message}` };
    }

    const sample = result.data[0] ?? {};
    if (!('usuario' in sample) || !('nome' in sample)) {
        return { rows: [], error: 'O CSV deve conter as colunas: usuario, nome.' };
    }

    const rows: CsvUsuarioRow[] = result.data.map((r) => ({
        usuario: r['usuario'] ?? '',
        senha: r['senha'] ?? '',
        nome: r['nome'] ?? '',
        nome_grupo: r['nome_grupo'] ?? '',
        ativo: r['ativo'] ?? '',
    }));

    return { rows, error: null };
}

function ImportarUsuariosCsvModal({
    grupos,
    grupoInicialId,
    onClose,
    onImported,
}: {
    grupos: RadiusUserGroupDTO[];
    /** Grupo do filtro da tela, se houver — pré-seleciona o destino das linhas sem nome_grupo. */
    grupoInicialId: number | null;
    onClose: () => void;
    onImported: () => void;
}) {
    const [file, setFile] = React.useState<File | null>(null);
    const [preview, setPreview] = React.useState<CsvUsuarioRow[]>([]);
    const [parseError, setParseError] = React.useState<string | null>(null);
    const [result, setResult] = React.useState<CsvImportResult | null>(null);
    const [dragging, setDragging] = React.useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [grupoSemNomeId, setGrupoSemNomeId] = React.useState<number>(
        grupoInicialId ?? grupos.find((g) => g.is_default)?.id ?? grupos[0]?.id ?? 0,
    );

    const grupoSemNome = grupos.find((g) => g.id === grupoSemNomeId) ?? null;
    const linhasSemGrupo = preview.filter((r) => !r.nome_grupo).length;

    const importMutation = useMutation(
        async () => {
            if (!file) return;
            const formData = new FormData();
            formData.append('file', file);
            if (grupoSemNomeId) formData.append('grupo_id', String(grupoSemNomeId));
            const res = await axios.post<CsvImportResult>(
                '/api/radius/users/import-csv',
                formData,
                { withCredentials: true },
            );
            return res.data;
        },
        {
            onSuccess: (data) => {
                if (!data) return;
                setResult(data);
                if (data.imported > 0) onImported();
            },
            onError: (err: unknown) => {
                const { message } = extractApiError(err, 'Erro ao importar CSV');
                toastWarning(message);
            },
        }
    );

    const canImport = !!file && !parseError && preview.length > 0 && !result
        && (linhasSemGrupo === 0 || !!grupoSemNome);

    const processFile = (f: File) => {
        setFile(f);
        setResult(null);
        setParseError(null);
        const reader = new FileReader();
        reader.onload = (ev) => {
            const { rows, error } = parseUsuariosCsvPreview(ev.target?.result as string);
            setParseError(error);
            setPreview(rows);
        };
        reader.readAsText(f);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragging(false);
        const f = e.dataTransfer.files?.[0];
        if (f) processFile(f);
    };

    const thStyle: React.CSSProperties = {
        padding: '7px 12px', textAlign: 'left', fontWeight: 600, fontSize: '12px',
        color: 'var(--tc-color-gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em',
        borderBottom: '1px solid var(--tc-color-gray-200)',
        position: 'sticky', top: 0, zIndex: 1,
        backgroundColor: 'var(--tc-color-gray-100)',
    };
    const tdStyle: React.CSSProperties = {
        padding: '7px 12px', fontSize: '13px', color: 'var(--tc-color-gray-800)',
        borderBottom: '1px solid var(--tc-color-gray-100)',
    };

    return (
        <Modal
            show
            ignoreOverlayClick
            title="Importar usuários RADIUS via CSV"
            size="lg"
            onClose={onClose}
            onCancel={result ? undefined : onClose}
            onConfirm={result ? onClose : () => importMutation.mutate()}
            confirmProps={{
                label: result ? 'Fechar' : importMutation.isLoading ? 'Importando…' : 'Importar',
                disabled: result ? false : (!canImport || importMutation.isLoading),
                variant: result ? 'outline-secondary' : 'primary',
            }}
            cancelProps={{ visible: !result }}
        >
            <VBox gap="16px" style={{ minWidth: '480px' }}>

                {/* ── Fase 1: seleção de arquivo ── */}
                {!result && (
                    <>
                        <div
                            onClick={() => fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={handleDrop}
                            style={{
                                border: `2px dashed ${dragging ? 'var(--tc-color-primary-400)' : file ? 'var(--tc-color-primary-300)' : 'var(--tc-color-gray-300)'}`,
                                borderRadius: '10px',
                                padding: '28px 20px',
                                textAlign: 'center',
                                cursor: 'pointer',
                                background: dragging ? 'var(--tc-color-primary-50)' : file ? 'var(--tc-color-primary-50)' : 'var(--tc-color-gray-50)',
                                transition: 'border-color 0.15s, background 0.15s',
                                userSelect: 'none',
                            }}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,text/csv"
                                style={{ display: 'none' }}
                                onChange={(e) => { const f = e.target.files?.[0]; if (f) processFile(f); }}
                            />
                            <Icon
                                path={mdiFileImport}
                                size="32px"
                                color={file ? 'var(--tc-color-primary-400)' : 'var(--tc-color-gray-400)'}
                                style={{ display: 'block', margin: '0 auto 10px' }}
                            />
                            {file ? (
                                <VBox gap="4px" style={{ alignItems: 'center' }}>
                                    <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--tc-color-primary-700)' }}>
                                        {file.name}
                                    </span>
                                    {!parseError && preview.length > 0 && (
                                        <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-500)' }}>
                                            {preview.length} linha{preview.length !== 1 ? 's' : ''} encontrada{preview.length !== 1 ? 's' : ''} · clique para trocar
                                        </span>
                                    )}
                                </VBox>
                            ) : (
                                <VBox gap="4px" style={{ alignItems: 'center' }}>
                                    <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--tc-color-gray-600)' }}>
                                        Arraste o arquivo aqui ou clique para selecionar
                                    </span>
                                    <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-400)' }}>
                                        Colunas obrigatórias: <strong>usuario</strong>, <strong>nome</strong> — vírgula ou ponto-e-vírgula
                                    </span>
                                    <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-400)' }}>
                                        Opcionais: <strong>senha</strong> (vazia = conta sem senha), <strong>nome_grupo</strong> (nome ou slug do grupo — linhas sem ele vão para o grupo escolhido abaixo), <strong>ativo</strong>, <strong>ip_fixo</strong>, <strong>gateway_ip</strong>, <strong>vlan</strong>
                                    </span>
                                </VBox>
                            )}
                        </div>

                        {parseError && (
                            <HBox gap="8px" style={{ alignItems: 'center', color: 'var(--tc-text-color-danger-emphasis)', background: 'var(--tc-background-color-danger-subtle)', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--tc-border-color-danger-subtle)' }}>
                                <Icon path={mdiAlertCircle} size="18px" style={{ flexShrink: 0 }} />
                                <span style={{ fontSize: '13px' }}>{parseError}</span>
                            </HBox>
                        )}

                        {/* Grupo de destino para linhas sem nome_grupo */}
                        {!parseError && linhasSemGrupo > 0 && (
                            <CampoSeletor
                                label={`Grupo para as ${linhasSemGrupo} linha${linhasSemGrupo !== 1 ? 's' : ''} sem nome_grupo *`}
                                value={grupoSemNomeId ? String(grupoSemNomeId) : ''}
                                onChange={(v) => setGrupoSemNomeId(Number(v))}
                                options={[
                                    ...(grupoSemNomeId ? [] : [{ id: '', label: 'Selecione um grupo…' }]),
                                    ...grupos.map((g) => ({ id: String(g.id), label: `${g.nome} (${g.slug})${g.is_default ? ' — padrão' : ''}` })),
                                ]}
                            />
                        )}

                        {/* Tabela de preview — senha nunca é exibida */}
                        {!parseError && preview.length > 0 && (
                            <div style={{ border: '1px solid var(--tc-color-gray-200)', borderRadius: '8px', maxHeight: '240px', overflowY: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>Usuário</th>
                                            <th style={thStyle}>Nome</th>
                                            <th style={thStyle}>Grupo</th>
                                            <th style={thStyle}>Senha</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.map((row, idx) => (
                                            <tr key={idx} style={{ background: idx % 2 === 1 ? 'var(--tc-color-gray-50)' : 'white' }}>
                                                <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '12px' }}>{row.usuario || <em style={{ color: 'var(--tc-color-gray-300)' }}>—</em>}</td>
                                                <td style={tdStyle}>{row.nome || <em style={{ color: 'var(--tc-color-gray-300)' }}>—</em>}</td>
                                                <td style={tdStyle}>{row.nome_grupo || (grupoSemNome ? <em style={{ color: 'var(--tc-color-gray-400)' }}>{grupoSemNome.nome}</em> : <em style={{ color: 'var(--tc-text-color-danger-emphasis)' }}>sem grupo</em>)}</td>
                                                <td style={{ ...tdStyle, color: 'var(--tc-color-gray-400)' }}>{row.senha ? '••••••' : <em style={{ color: 'var(--tc-color-gray-400)' }}>sem senha</em>}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}

                {/* ── Fase 2: resultado ── */}
                {result && (
                    <VBox gap="14px">
                        <HBox gap="12px" style={{
                            alignItems: 'center',
                            background: result.imported > 0 ? 'var(--tc-background-color-success-subtle)' : 'var(--tc-color-gray-100)',
                            border: `1px solid ${result.imported > 0 ? 'var(--tc-border-color-success-subtle)' : 'var(--tc-color-gray-200)'}`,
                            padding: '14px 16px', borderRadius: '10px',
                        }}>
                            <Icon
                                path={mdiCheckCircle}
                                size="28px"
                                color={result.imported > 0 ? 'var(--status-ok)' : 'var(--tc-color-gray-400)'}
                                style={{ flexShrink: 0 }}
                            />
                            <VBox gap="2px">
                                <span style={{ fontWeight: 700, fontSize: '15px', color: result.imported > 0 ? 'var(--tc-text-color-success-emphasis)' : 'var(--tc-color-gray-600)' }}>
                                    {result.imported} usuário{result.imported !== 1 ? 's' : ''} importado{result.imported !== 1 ? 's' : ''} com sucesso
                                </span>
                                {result.errors.length > 0 && (
                                    <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-500)' }}>
                                        {result.errors.length} linha{result.errors.length !== 1 ? 's' : ''} ignorada{result.errors.length !== 1 ? 's' : ''} por erro
                                    </span>
                                )}
                            </VBox>
                        </HBox>

                        {result.errors.length > 0 && (
                            <VBox gap="8px">
                                <HBox gap="6px" style={{ alignItems: 'center' }}>
                                    <Icon path={mdiAlertCircle} size="16px" color="var(--tc-text-color-danger-emphasis)" />
                                    <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--tc-text-color-danger-emphasis)' }}>
                                        Linhas com erro
                                    </span>
                                </HBox>
                                <div style={{ border: '1px solid var(--tc-border-color-danger-subtle)', borderRadius: '8px', maxHeight: '220px', overflowY: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ ...thStyle, width: '52px', textAlign: 'center' }}>#</th>
                                                <th style={thStyle}>Usuário</th>
                                                <th style={thStyle}>Nome</th>
                                                <th style={thStyle}>Motivo</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {result.errors.map((e) => (
                                                <tr key={e.linha} style={{ background: 'var(--tc-background-color-danger-subtle)', borderBottom: '1px solid var(--tc-border-color-danger-subtle)' }}>
                                                    <td style={{ ...tdStyle, textAlign: 'center', color: 'var(--tc-color-gray-400)', fontVariantNumeric: 'tabular-nums' }}>{e.linha}</td>
                                                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '12px' }}>{e.usuario || <em style={{ color: 'var(--tc-color-gray-400)' }}>—</em>}</td>
                                                    <td style={tdStyle}>{e.nome || <em style={{ color: 'var(--tc-color-gray-400)' }}>—</em>}</td>
                                                    <td style={{ ...tdStyle, color: 'var(--tc-text-color-danger-emphasis)' }}>{e.erro}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </VBox>
                        )}
                    </VBox>
                )}
            </VBox>
        </Modal>
    );
}

// ─── Exportar usuários via CSV ─────────────────────────────────────────────────

function ExportarUsuariosCsvModal({
    total,
    exportando,
    onConfirm,
    onClose,
}: {
    /** Quantidade de usuários no filtro atual (é o que vai pro arquivo). */
    total: number;
    exportando: boolean;
    onConfirm: (incluirSenhas: boolean) => void;
    onClose: () => void;
}) {
    // Senhas ficam de fora por padrão: o arquivo sai em texto claro e é a
    // única forma de a senha RADIUS deixar o sistema. Quem marca, sabe.
    const [incluirSenhas, setIncluirSenhas] = React.useState(false);

    return (
        <Modal
            show
            title="Exportar usuários RADIUS em CSV"
            onClose={onClose}
            onCancel={onClose}
            onConfirm={() => onConfirm(incluirSenhas)}
            confirmProps={{
                label: exportando ? 'Exportando…' : 'Exportar',
                disabled: exportando,
            }}
        >
            <VBox gap="16px" style={{ minWidth: '420px' }}>
                <Descricao>
                    {total === 1
                        ? '1 usuário será exportado, conforme os filtros atuais da listagem.'
                        : `${total} usuários serão exportados, conforme os filtros atuais da listagem.`}
                    {' '}O arquivo pode ser reimportado em "Importar CSV".
                </Descricao>

                <CampoCheckbox
                    text="Incluir senhas"
                    value={incluirSenhas}
                    onChange={setIncluirSenhas}
                />

                {incluirSenhas && (
                    <HBox gap="8px" style={{ alignItems: 'flex-start' }}>
                        <Icon
                            path={mdiAlertCircle}
                            size="18px"
                            color="var(--tc-text-color-danger-emphasis)"
                            style={{ flexShrink: 0 }}
                        />
                        <Descricao>
                            As senhas saem em texto claro no arquivo. Guarde-o em local seguro
                            e apague quando não precisar mais.
                        </Descricao>
                    </HBox>
                )}
            </VBox>
        </Modal>
    );
}

// ─── Página principal ─────────────────────────────────────────────────────────

export default function RadiusPage() {
    const [, , isMobile] = useWindowSize();
    const qc = useQueryClient();

    const [showGruposModal, setShowGruposModal] = React.useState(false);
    const [showTentativas, setShowTentativas] = React.useState(false);
    const [showUsuarioModal, setShowUsuarioModal] = React.useState(false);
    const [showImportCsvModal, setShowImportCsvModal] = React.useState(false);
    const [showExportCsvModal, setShowExportCsvModal] = React.useState(false);
    const [editUsuario, setEditUsuario] = React.useState<RadiusUserDTO | null>(null);
    const [filtroGrupoId, setFiltroGrupoId] = React.useState<number | ''>('');
    const [filtroTexto, setFiltroTexto] = React.useState('');
    const [filtroAtivo, setFiltroAtivo] = React.useState<'' | 'true' | 'false'>('');
    const [filtroConexao, setFiltroConexao] = React.useState<'' | RadiusUserConexaoFiltro>('');
    const filtroTextoDebounced: string = useDebounce(filtroTexto, 500);

    const queryGroups = useQuery(['radius-groups'], async () => {
        const res = await axios.get<{ ok: boolean; result: RadiusUserGroupDTO[] }>(
            '/api/radius/groups',
            { withCredentials: true },
        );
        return res.data.result ?? [];
    });

    // Filtros da listagem como query string — compartilhado entre a listagem
    // paginada e a exportação CSV, que exporta exatamente o que a tela mostra.
    const montarParamsFiltro = React.useCallback(() => {
        const params = new URLSearchParams();
        if (filtroTextoDebounced) params.set('txt', filtroTextoDebounced);
        if (filtroGrupoId) params.set('group_id', String(filtroGrupoId));
        if (filtroAtivo) params.set('ativo', filtroAtivo);
        if (filtroConexao) params.set('conexao', filtroConexao);
        return params;
    }, [filtroTextoDebounced, filtroGrupoId, filtroAtivo, filtroConexao]);

    // Listagem paginada por cursor (GET /radius/users); filtros resolvidos no
    // servidor. A chave começa com 'radius-users' para que os
    // invalidateQueries(['radius-users']) dos modais continuem valendo.
    const queryUsers = useInfiniteQuery({
        queryKey: ['radius-users', filtroGrupoId, filtroTextoDebounced, filtroAtivo, filtroConexao],
        queryFn: async ({ pageParam }) => {
            const params = montarParamsFiltro();
            if (pageParam) params.set('c', String(pageParam));
            const res = await axios.get<{ ok: boolean } & RadiusUserListDTO>(
                `/api/radius/users?${params.toString()}`,
                { withCredentials: true },
            );
            return res.data;
        },
        getNextPageParam: (lastPage) => lastPage.cursor ?? null,
        keepPreviousData: true,
    });

    const usuarios = React.useMemo(
        () => queryUsers.data?.pages.flatMap((p) => p.result ?? []) ?? [],
        [queryUsers.data],
    );
    const totalUsuarios = queryUsers.data?.pages[queryUsers.data.pages.length - 1]?.total ?? 0;

    // Status de conexão só das linhas carregadas — a chave muda conforme o
    // scroll traz páginas novas, e o polling mantém o "online há X" vivo.
    const usuarioIds = React.useMemo(() => usuarios.map((u) => u.id!).filter(Boolean), [usuarios]);
    const queryStatus = useQuery(
        ['radius-status', usuarioIds],
        async () => {
            if (!usuarioIds.length) return [];
            const res = await axios.post<{ ok: boolean; result: RadiusUserStatusDTO[] }>(
                '/api/radius/status',
                { user_ids: usuarioIds },
                { withCredentials: true },
            );
            return res.data.result ?? [];
        },
        { refetchInterval: 15_000, keepPreviousData: true },
    );

    const statusMap = new Map<number, RadiusUserStatusDTO>(
        (queryStatus.data ?? []).map((s) => [s.user_id, s]),
    );

    const queryGateways = useQuery(
        ['radius-users-gateways'],
        async () => {
            const res = await axios.get<{ ok: boolean; result: string[] }>(
                '/api/radius/users/gateways',
                { withCredentials: true },
            );
            return res.data.result ?? [];
        },
        { enabled: showUsuarioModal },
    );

    const mutDelete = useMutation({
        mutationFn: async ({ grupoId, userId }: { grupoId: number; userId: number }) => {
            await axios.delete(`/api/radius/groups/${grupoId}/users/${userId}`, {
                withCredentials: true,
            });
        },
        onSuccess: () => {
            toastSuccess('Usuário removido!');
            qc.invalidateQueries(['radius-users']);
        },
        onError: (err) => {
            const { message } = extractApiError(err, 'Erro ao remover usuário');
            toastError(message);
        },
    });

    const grupos = queryGroups.data ?? [];
    const grupoOptions = grupos.map((g) => ({ id: String(g.id), label: `${g.nome} (${g.slug})` }));
    const grupoMap = Object.fromEntries(grupos.map((g) => [g.id!, g]));

    // Exportação CSV: baixa via axios (cookie de sessão + mesma resolução de
    // URL das outras chamadas) e dispara o download com um blob URL.
    const exportarCsv = useMutation(
        async ({ incluirSenhas }: { incluirSenhas: boolean }) => {
            const params = montarParamsFiltro();
            if (incluirSenhas) params.set('senhas', '1');
            const res = await axios.get<Blob>(
                `/api/radius/users/export-csv?${params.toString()}`,
                { withCredentials: true, responseType: 'blob' },
            );
            const disposition = String(res.headers['content-disposition'] ?? '');
            const nomeArquivo = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'radius-usuarios.csv';
            const url = URL.createObjectURL(res.data);
            try {
                const a = document.createElement('a');
                a.href = url;
                a.download = nomeArquivo;
                document.body.appendChild(a);
                a.click();
                a.remove();
            } finally {
                URL.revokeObjectURL(url);
            }
        },
        {
            onSuccess: () => setShowExportCsvModal(false),
            onError: (err) => {
                const { message } = extractApiError(err, 'Erro ao exportar CSV');
                toastError(message);
            },
        },
    );

    const handleNovoUsuario = () => {
        setEditUsuario(null);
        setShowUsuarioModal(true);
    };

    const handleEditUsuario = (u: RadiusUserDTO) => {
        setEditUsuario(u);
        setShowUsuarioModal(true);
    };

    const cabecalhos: DataTableHeader[] = [
        {
            label: 'Nome',
            // Usuários criados pelo autocadastro do portal cativo trazem o
            // restante do formulário em metadados — mostrado como tooltip.
            render: (u) => {
                const meta = u.metadados as Record<string, string> | null | undefined;
                const entradas = meta ? Object.entries(meta).filter(([, v]) => v !== '') : [];
                if (!entradas.length) return u.nome;
                return (
                    <span title={entradas.map(([k, v]) => `${k}: ${v}`).join('\n')} style={{ cursor: 'help', borderBottom: '1px dotted currentColor' }}>
                        {u.nome}
                    </span>
                );
            },
        },
        {
            label: 'Username',
            render: (u) => {
                const g = grupoMap[u.grupo_id];
                if (!g || g.is_default) {
                    return <span style={{ userSelect: 'all' }}>{u.username}</span>;
                }
                return (
                    <span>
                        <span style={{ color: 'var(--tc-color-text-muted, #999)', userSelect: 'all' }}>{g.slug}\</span>
                        <span style={{ userSelect: 'all' }}>{u.username}</span>
                    </span>
                );
            },
        },
        {
            label: 'Grupo',
            render: (u) => {
                const g = grupoMap[u.grupo_id];
                return g ? g.nome : `#${u.grupo_id}`;
            },
        },
        { label: 'Status', render: (u) => <StatusBadge ativo={u.ativo} /> },
        {
            label: 'Conexão',
            render: (u) => {
                const st = statusMap.get(u.id!);
                if (!st) return <StatusDot status="unknown" showLabel={false} />;
                if (st.status === 'online') {
                    return (
                        <StatusDot
                            status="online"
                            label={st.online_seconds != null ? `online há ${formatDuration(st.online_seconds)}` : 'online'}
                        />
                    );
                }
                return (
                    <StatusDot
                        status="offline"
                        label={st.offline_seconds != null ? `offline há ${formatDuration(st.offline_seconds)}` : 'offline'}
                    />
                );
            },
        },
        {
            label: '',
            render: (u) => (
                <HBox style={{ gap: '6px', justifyContent: 'flex-end' }}>
                    <Botao
                        variant="outline-primary"
                        size="sm"
                        icon={mdiPencil}
                        onClick={() => handleEditUsuario(u)}
                    />
                    <Botao
                        variant="outline-danger"
                        size="sm"
                        icon={mdiTrashCan}
                        disabled={mutDelete.isLoading}
                        onClick={() => {
                            if (confirm(`Remover usuário "${u.nome}" (${u.username})?`)) {
                                mutDelete.mutate({ grupoId: u.grupo_id, userId: u.id! });
                            }
                        }}
                    />
                </HBox>
            ),
        },
    ];

    return (
        <LayoutUsuario>
            {/* Desktop: a tabela é o container que rola (cabeçalho e filtros fixos).
                Mobile: cabeçalho + filtros ocupam quase toda a viewport, então a
                página inteira rola e a tabela cresce livre — o sentinela do
                DataTable cuida do infinite scroll nos dois casos. */}
            <VBox style={isMobile
                ? { gap: '16px', padding: '16px' }
                : { gap: '16px', padding: '16px', height: '100%', overflow: 'hidden' }}
            >
                <PageHeader
                    title="Usuários RADIUS"
                    description={<>Gerencie usuários para autenticação RADIUS. O formato de login é <code>grupo\usuário</code>.</>}
                    actions={(
                        <>
                            <Botao
                                variant="outline-secondary"
                                icon={mdiClipboardTextClock}
                                onClick={() => setShowTentativas(true)}
                                style={isMobile ? { flex: '1 1 180px' } : undefined}
                            >
                                Tentativas
                            </Botao>
                            <Botao
                                variant="outline-secondary"
                                icon={mdiAccountGroup}
                                onClick={() => setShowGruposModal(true)}
                                style={isMobile ? { flex: '1 1 180px' } : undefined}
                            >
                                Gerenciar grupos
                            </Botao>
                            <Botao
                                variant="outline-secondary"
                                icon={mdiFileImport}
                                onClick={() => setShowImportCsvModal(true)}
                                disabled={queryGroups.isLoading}
                                style={isMobile ? { flex: '1 1 180px' } : undefined}
                            >
                                Importar CSV
                            </Botao>
                            <Botao
                                variant="outline-secondary"
                                icon={mdiFileExport}
                                onClick={() => setShowExportCsvModal(true)}
                                disabled={totalUsuarios === 0}
                                style={isMobile ? { flex: '1 1 180px' } : undefined}
                            >
                                Exportar CSV
                            </Botao>
                            <Botao
                                variant="primary"
                                icon={mdiPlus}
                                onClick={handleNovoUsuario}
                                disabled={queryGroups.isLoading}
                                style={isMobile ? { flex: '1 1 160px' } : undefined}
                            >
                                Novo usuário
                            </Botao>
                        </>
                    )}
                />

                {/* Filtros — texto (nome/usuário) é debounced; os demais aplicam na hora */}
                <HBox style={{ gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div style={{ flex: isMobile ? '1 1 100%' : '1 1 260px' }}>
                        <CampoTexto
                            label="Pesquisar"
                            prepend={<Icon path={mdiMagnify} size={1} color="var(--tc-color-gray-500)" />}
                            placeholder="nome ou usuário"
                            value={filtroTexto}
                            onChange={setFiltroTexto}
                        />
                    </div>
                    <div style={{ flex: isMobile ? '1 1 100%' : '0 1 240px' }}>
                        <CampoSeletor
                            label="Grupo"
                            value={filtroGrupoId?.toString()}
                            onChange={(v) => setFiltroGrupoId(v ? Number(v) : '')}
                            options={[{ id: '', label: 'Todos os grupos' }, ...grupoOptions]}
                        />
                    </div>
                    <div style={{ flex: isMobile ? '1 1 calc(50% - 6px)' : '0 1 150px' }}>
                        <CampoSeletor
                            label="Status"
                            value={filtroAtivo}
                            onChange={(v) => setFiltroAtivo((v ?? '') as '' | 'true' | 'false')}
                            options={[
                                { id: '', label: 'Todos' },
                                { id: 'true', label: 'Ativo' },
                                { id: 'false', label: 'Inativo' },
                            ]}
                        />
                    </div>
                    <div style={{ flex: isMobile ? '1 1 calc(50% - 6px)' : '0 1 150px' }}>
                        <CampoSeletor
                            label="Conexão"
                            value={filtroConexao}
                            onChange={(v) => setFiltroConexao((v ?? '') as '' | RadiusUserConexaoFiltro)}
                            options={[
                                { id: '', label: 'Todos' },
                                { id: 'online', label: 'Online' },
                                { id: 'offline', label: 'Offline' },
                            ]}
                        />
                    </div>
                </HBox>

                {queryUsers.isFetching && !queryUsers.isFetchingNextPage ? (
                    <Descricao>
                        Aguarde...{' '}
                        <Icon path={mdiLoading} size="14px" color="var(--tc-color-gray-700)" spin />
                    </Descricao>
                ) : (
                    <Descricao>Exibindo {usuarios.length} de {totalUsuarios} usuários.</Descricao>
                )}

                <DataTable
                    cabecalhos={cabecalhos}
                    linhas={usuarios}
                    isLoading={queryUsers.isFetching}
                    hasMoreData={queryUsers.hasNextPage}
                    onLoadMore={() => {
                        if (queryUsers.hasNextPage && !queryUsers.isFetchingNextPage) {
                            queryUsers.fetchNextPage();
                        }
                    }}
                />
            </VBox>

            <TentativasModal show={showTentativas} onClose={() => setShowTentativas(false)} />

            {showGruposModal && (
                <GruposManagerModal
                    onClose={() => {
                        setShowGruposModal(false);
                        qc.invalidateQueries(['radius-groups']);
                        qc.invalidateQueries(['radius-users']);
                    }}
                />
            )}

            {showExportCsvModal && (
                <ExportarUsuariosCsvModal
                    total={totalUsuarios}
                    exportando={exportarCsv.isLoading}
                    onConfirm={(incluirSenhas) => exportarCsv.mutate({ incluirSenhas })}
                    onClose={() => setShowExportCsvModal(false)}
                />
            )}
            {showImportCsvModal && (
                <ImportarUsuariosCsvModal
                    grupos={grupos}
                    grupoInicialId={filtroGrupoId !== '' ? Number(filtroGrupoId) : null}
                    onClose={() => setShowImportCsvModal(false)}
                    onImported={() => qc.invalidateQueries(['radius-users'])}
                />
            )}

            <UsuarioModal
                key={editUsuario?.id ?? 'new'}
                show={showUsuarioModal}
                usuario={editUsuario}
                grupos={grupos}
                gatewaysEmUso={queryGateways.data ?? []}
                grupoId={filtroGrupoId !== '' ? Number(filtroGrupoId) : (grupos[0]?.id ?? 0)}
                onClose={() => {
                    setShowUsuarioModal(false);
                    setEditUsuario(null);
                    qc.invalidateQueries(['radius-users']);
                }}

            />
        </LayoutUsuario>
    );
}
